import { CommonModule } from '@angular/common';
import {
  Component,
  ViewChild,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Table, TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { Filtering } from 'src/app/service/filtering/filtering';
import { RequestHandler } from 'src/app/service/OffService/request-handler';
import { environment } from 'src/environments/environment';

export type SelectionMode = 'single' | 'multiple' | 'checkbox';
export type ColumnTransform = 'uppercase' | 'lowercase' | 'date' | 'number';

export interface TableOptions {
  selectionMode?: SelectionMode;
  enabledFilterColumn?: boolean;
  enabledFilterGloblal?: boolean;
  enabledEditing?: boolean;
  showBtnAdd?: boolean;
  showBtnDelete?: boolean;
  showBtnEdit?: boolean;
  showBtnDuplicate?: boolean;
  showBtnExport?: boolean;
  showBtnImport?: boolean;
  showBtnRefresh?: boolean;
  showBtnView?: boolean;
  showCustomActions?: boolean;
  paginator?: boolean;
  rows?: number;
  rowsPerPageOptions?: number[];
  dataKey?: string;
  rowHover?: boolean;
  showHeader?: boolean;
  emptyMessage?: string;
  globalFilterFields?: string[];
}

export interface TableColumn {
  field: string;
  header: string;
  visible?: boolean;
  width?: string;
  sortable?: boolean;
  filterable?: boolean;
  editable?: boolean;
  headerClass?: string;
  bodyClass?: string;
  transform?: ColumnTransform;
  valueGetter?: (rowData: any) => any;
  format?: (value: any, rowData: any) => string;
  filterPlaceholder?: string;
}

export interface TableCustomAction {
  label: string;
  icon: string;
  styleClass?: string;
  action: (rowData: any) => void;
}

@Component({
  selector: 'app-common-table',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    ToolbarModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule
  ],
  templateUrl: './common-table.component.html',
  styleUrls: ['./common-table.component.scss'],
})
export class CommonTableComponent<T> {
  @ViewChild('dt') table?: Table;

  request = input<RequestHandler<any>>();
  filtering = input <Filtering<T>> (new Filtering<any>());

  cols = input.required<TableColumn[]>();
  customData = input<any[]>([]);
  options = input<TableOptions>({
    selectionMode: 'single',
    enabledFilterColumn: false,
    enabledFilterGloblal: true,
    enabledEditing: true,
    showBtnAdd: true,
    showBtnDelete: true,
    showBtnEdit: true,
    showBtnDuplicate: false,
    showBtnExport: true,
    showBtnImport: false,
    showBtnRefresh: false,
    showBtnView: true,
    showCustomActions: false,
    paginator: true,
    rows: 25,
    rowsPerPageOptions: [10, 25, 50, 100],
    rowHover: true,
    showHeader: true,
    emptyMessage: 'Sin resultados',
  });

  customActions = input<TableCustomAction[]>([]);

  actionClick = output<{ action: string; rowData?: any }>();
  actionFilter = output<string>();
  columnFilter = output<{ field: string; value: string }>();
  selectionChange = output<any>();

  globalFilterValue = signal('');
  selectionValue = signal<any | any[] | null>(null);

  data = signal<any[]>([]);

  env = environment;

  ngOnInit() {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.data.set([]);
  }

  visibleColumns = computed(() =>
    this.cols().filter((col) => col.visible !== false),
  );

  tableSelectionMode = computed(() => {
    const mode = this.options().selectionMode ?? 'single';
    return mode === 'checkbox' ? 'multiple' : mode;
  });

  showCheckboxes = computed(() => this.options().selectionMode === 'checkbox');

  showRowActions = computed(() => {
    const opt = this.options();
    return (
      !!opt.showBtnEdit ||
      !!opt.showBtnDelete ||
      !!opt.showBtnView ||
      !!opt.showBtnDuplicate ||
      (!!opt.showCustomActions && this.customActions().length > 0)
    );
  });

  globalFilterFields = computed(() => {
    const optFields = this.options().globalFilterFields;
    if (optFields && optFields.length > 0) {
      return optFields;
    }
    return this.visibleColumns()
      .filter((col) => col.filterable !== false)
      .map((col) => col.field);
  });

  onActionClick(action: string, rowData?: any) {
    this.actionClick.emit({ action, rowData });
  }

  onGlobalFilter(event: Event) {
    const value = (event.target as HTMLInputElement).value ?? '';
    this.globalFilterValue.set(value);
    this.actionFilter.emit(value);
    this.table?.filterGlobal(value, 'contains');
  }

  onColumnFilter(event: Event, field: string) {
    const value = (event.target as HTMLInputElement).value ?? '';
    this.table?.filter(value, field, 'contains');
    this.columnFilter.emit({ field, value });
  }

  onSelectionChange(value: any) {
    this.selectionValue.set(value ?? null);
    this.selectionChange.emit(value ?? null);
  }

  formatCell(rowData: any, column: TableColumn): string {
    const rawValue = column.valueGetter
      ? column.valueGetter(rowData)
      : this.resolveFieldValue(rowData, column.field);
    if (column.format) {
      return column.format(rawValue, rowData);
    }
    if (rawValue === null || rawValue === undefined) {
      return '';
    }
    switch (column.transform) {
      case 'uppercase':
        return String(rawValue).toUpperCase();
      case 'lowercase':
        return String(rawValue).toLowerCase();
      case 'number':
        return Number(rawValue).toLocaleString();
      case 'date':
        return new Date(rawValue).toLocaleDateString();
      default:
        return String(rawValue);
    }
  }

  loadData() {
    if (this.request()) {
      this.request()!.safePerform();
      this.request()!.response((data: any[]) => {
        this.data.set(data);
      });
    }
  }

  initFiltering() {
    this.filtering().setValue(this.data());
    // this.filtering().addFilter((value, self) => {
    //   const term = self.filters.global.toLowerCase();
    //   if (!term) {
    //     return value;
    //   }
    //   return value.filter((item) => {
    //     return self
    //       .globalFilterFields()
    //       .some((field) => {
    //         const fieldValue = String(this.resolveFieldValue(item.data, field) ?? '').toLowerCase();
    //         return fieldValue.includes(term);
    //       });
    //   });
    
  }

  private resolveFieldValue(rowData: any, field: string): any {
    if (!rowData || !field) {
      return null;
    }
    return field
      .split('.')
      .reduce((acc, key) => (acc ? acc[key] : null), rowData);
  }
}
